import { useEffect, useRef } from "react";
import * as THREE from "three";
import { SVGLoader } from "three/examples/jsm/loaders/SVGLoader.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import "./Hero3DLogo.css";

const FRICTION = 0.985;
const SPRING = 0.0025;
const SETTLE_THRESHOLD = 0.0004;
const CLICK_THRESHOLD_PX = 4;

const TAU = Math.PI * 2;
const normalizeAngle = (a) => {
  let n = a % TAU;
  if (n > Math.PI) n -= TAU;
  if (n < -Math.PI) n += TAU;
  return n;
};

const isWebGLAvailable = () => {
  try {
    const canvas = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (canvas.getContext("webgl2") || canvas.getContext("webgl")));
  } catch {
    return false;
  }
};

export default function Hero3DLogo() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    if (!isWebGLAvailable()) return;

    const width = mount.clientWidth;
    const height = mount.clientHeight;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch {
      return;
    }

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(40, width / height, 0.1, 1000);
    camera.position.set(0, 0, 600);

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.9;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);

    // Environment map for reflections (fixed in world space)
    const pmrem = new THREE.PMREMGenerator(renderer);
    pmrem.compileEquirectangularShader();
    const envScene = new RoomEnvironment();
    const envMap = pmrem.fromScene(envScene, 0.04).texture;
    scene.environment = envMap;

    // Fixed-in-world-space lighting — does NOT rotate with the logo
    const ambient = new THREE.AmbientLight(0xffffff, 0.22);
    scene.add(ambient);

    const keyLight = new THREE.DirectionalLight(0xffffff, 1.4);
    keyLight.position.set(250, 350, 400);
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0x80c7ff, 0.55);
    fillLight.position.set(-300, -120, 250);
    scene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0xffffff, 0.9);
    rimLight.position.set(0, -200, -400);
    scene.add(rimLight);

    const accentLight = new THREE.PointLight(0x0092ff, 1.1, 1200);
    accentLight.position.set(-200, 150, 300);
    scene.add(accentLight);

    const group = new THREE.Group();
    scene.add(group);

    const loader = new SVGLoader();
    loader.load("/niertech-3d.svg", (data) => {
      const paths = data.paths;
      const meshGroup = new THREE.Group();

      paths.forEach((path) => {
        const fillColor = path.userData?.style?.fill;
        const color = new THREE.Color(
          fillColor && fillColor !== "none" ? fillColor : "#0092ff"
        );

        const material = new THREE.MeshPhysicalMaterial({
          color,
          metalness: 0.55,
          roughness: 0.22,
          clearcoat: 1.0,
          clearcoatRoughness: 0.08,
          reflectivity: 0.6,
          envMapIntensity: 0.85,
          side: THREE.DoubleSide,
        });

        const shapes = SVGLoader.createShapes(path);
        shapes.forEach((shape) => {
          const geometry = new THREE.ExtrudeGeometry(shape, {
            depth: 60,
            bevelEnabled: true,
            bevelThickness: 4,
            bevelSize: 3,
            bevelOffset: 0,
            bevelSegments: 4,
            curveSegments: 24,
          });
          const mesh = new THREE.Mesh(geometry, material);
          meshGroup.add(mesh);
        });
      });

      // SVG Y is flipped vs Three's Y
      meshGroup.scale.set(1, -1, 1);

      // Center on origin
      const box = new THREE.Box3().setFromObject(meshGroup);
      const center = box.getCenter(new THREE.Vector3());
      meshGroup.position.sub(center);

      // Fit to camera
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const fitScale = 340 / maxDim;
      meshGroup.scale.multiplyScalar(fitScale);

      group.add(meshGroup);
      group.rotation.x = 0;
      group.rotation.y = 0;
    });

    // Drag + inertia state
    const velocity = { x: 0, y: 0 };
    let dragging = false;
    let moved = false;
    let downAt = { x: 0, y: 0 };
    let last = { x: 0, y: 0, t: 0 };

    const el = renderer.domElement;
    el.style.touchAction = "none";
    el.style.cursor = "grab";

    const onPointerDown = (e) => {
      dragging = true;
      moved = false;
      downAt = { x: e.clientX, y: e.clientY };
      last = { x: e.clientX, y: e.clientY, t: performance.now() };
      velocity.x = 0;
      velocity.y = 0;
      el.setPointerCapture?.(e.pointerId);
      el.style.cursor = "grabbing";
    };

    const onPointerMove = (e) => {
      if (!dragging) return;
      const now = performance.now();
      const dx = e.clientX - last.x;
      const dy = e.clientY - last.y;
      const dt = Math.max(1, now - last.t);

      if (
        Math.abs(e.clientX - downAt.x) > CLICK_THRESHOLD_PX ||
        Math.abs(e.clientY - downAt.y) > CLICK_THRESHOLD_PX
      ) {
        moved = true;
      }

      const rotDY = dy * 0.008;
      const rotDX = dx * 0.008;

      group.rotation.x += rotDY;
      group.rotation.y += rotDX;

      velocity.x = (rotDY / dt) * 16;
      velocity.y = (rotDX / dt) * 16;

      last = { x: e.clientX, y: e.clientY, t: now };
    };

    const onPointerUp = (e) => {
      if (!dragging) return;
      dragging = false;
      el.releasePointerCapture?.(e.pointerId);
      el.style.cursor = "grab";

      if (!moved) {
        const dirX = Math.random() > 0.5 ? 1 : -1;
        const dirY = Math.random() > 0.5 ? 1 : -1;
        velocity.x = dirX * (0.04 + Math.random() * 0.06);
        velocity.y = dirY * (0.12 + Math.random() * 0.18);
      }
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);

    // Resize
    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(mount);

    // Render loop — spring back toward camera when idle
    let raf;
    const tick = () => {
      if (!dragging) {
        const offsetX = normalizeAngle(group.rotation.x);
        const offsetY = normalizeAngle(group.rotation.y);

        velocity.x += -offsetX * SPRING;
        velocity.y += -offsetY * SPRING;

        velocity.x *= FRICTION;
        velocity.y *= FRICTION;

        const settled =
          Math.abs(velocity.x) < SETTLE_THRESHOLD &&
          Math.abs(velocity.y) < SETTLE_THRESHOLD &&
          Math.abs(offsetX) < 0.003 &&
          Math.abs(offsetY) < 0.003;

        if (settled) {
          group.rotation.x = 0;
          group.rotation.y = 0;
          velocity.x = 0;
          velocity.y = 0;
        } else {
          group.rotation.x += velocity.x;
          group.rotation.y += velocity.y;
        }
      }
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
      pmrem.dispose();
      renderer.dispose();
      if (el.parentNode) el.parentNode.removeChild(el);
    };
  }, []);

  return <div ref={mountRef} className="hero3d" />;
}
